/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createSingularityVFX(
  name: string = "Singularity",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "singularity",
    name: "Singularity",
    nodes: [
      // Continuous spawn for accretion disk flow
      {
        id: "spawn_rate",
        type: "vfx.spawn.rate",
        position: { x: 50, y: 50 },
        inputValues: { rate: 800 },
        contextId: "ctx_spawn",
      },

      // === INIT CONTEXT NODES ===

      // Spawn on outer ring (circle shape)
      {
        id: "init_position",
        type: "vfx.init.position_shape",
        position: { x: 50, y: 50 },
        inputValues: {
          shape: 1, // Circle
          radius: 150,
          arc: 360,
          center: { x: 0, y: 0, z: 0 },
          size: { x: 1, y: 1, z: 1 },
        },
        contextId: "ctx_init",
      },
      // Lifetime for spiral journey
      {
        id: "init_lifetime",
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 150 },
        inputValues: { min: 2.5, max: 4.0 },
        contextId: "ctx_init",
      },
      // Small initial size
      {
        id: "init_size",
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 1.5, max: 3 },
        contextId: "ctx_init",
      },
      // Hot plasma colors - orange/red HDR
      {
        id: "init_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 4.0, g: 1.5, b: 0.3, a: 1 } }, // Bright orange HDR
              { t: 0.3, color: { r: 3.5, g: 0.8, b: 0.2, a: 1 } }, // Orange-red HDR
              { t: 0.6, color: { r: 5.0, g: 2.0, b: 0.5, a: 1 } }, // Golden HDR
              { t: 1, color: { r: 3.0, g: 0.5, b: 0.1, a: 1 } }, // Deep red HDR
            ],
          },
        },
        contextId: "ctx_init",
      },
      // Small initial orbital velocity (tangent to circle)
      {
        id: "init_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 450 },
        inputValues: {
          direction: { x: 0, y: 0, z: 0 },
          speed: 0,
          spread: 0,
        },
        contextId: "ctx_init",
      },
      // Random rotation for variety
      {
        id: "init_angular",
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 550 },
        inputValues: { speed: 180, variance: 90 },
        contextId: "ctx_init",
      },

      // === UPDATE CONTEXT NODES ===

      // Vortex - spiral rotation around center
      {
        id: "update_vortex",
        type: "vfx.update.vortex",
        position: { x: 50, y: 50 },
        inputValues: {
          center: { x: 0, y: 0, z: 0 },
          axis: { x: 0, y: 0, z: 1 },
          strength: 80,
          pullStrength: 35, // Inward pull
        },
        contextId: "ctx_update",
      },
      // Additional attraction for stronger gravity feel
      {
        id: "update_attract",
        type: "vfx.update.attract",
        position: { x: 50, y: 150 },
        inputValues: {
          target: { x: 0, y: 0, z: 0 },
          strength: 25,
          maxDistance: 200,
          falloff: 1, // Quadratic
        },
        contextId: "ctx_update",
      },
      // Color intensifies toward center (over lifetime)
      {
        id: "update_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0.0, color: { r: 3.0, g: 0.8, b: 0.2, a: 0.8 } }, // Orange at edge
              { t: 0.3, color: { r: 5.0, g: 1.5, b: 0.3, a: 1.0 } }, // Brighter
              { t: 0.5, color: { r: 8.0, g: 3.0, b: 0.8, a: 1.0 } }, // Intense yellow
              { t: 0.7, color: { r: 12.0, g: 8.0, b: 4.0, a: 1.0 } }, // Near white HDR
              { t: 0.85, color: { r: 20.0, g: 18.0, b: 15.0, a: 1.0 } }, // Blinding white HDR
              { t: 1.0, color: { r: 25.0, g: 25.0, b: 25.0, a: 0.0 } }, // Flash out
            ],
          },
        },
        contextId: "ctx_update",
      },
      // Size grows as approaching event horizon then shrinks
      {
        id: "update_size",
        type: "vfx.update.size_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, value: 1.0 },
              { t: 0.4, value: 1.2 },
              { t: 0.7, value: 2.5 },
              { t: 0.9, value: 4.0 },
              { t: 1.0, value: 0.0 },
            ],
          },
        },
        contextId: "ctx_update",
      },
      // Add some turbulence
      {
        id: "update_turbulence",
        type: "vfx.update.turbulence",
        position: { x: 50, y: 450 },
        inputValues: {
          intensity: 15,
          frequency: 2,
          octaves: 2,
        },
        contextId: "ctx_update",
      },

      // === OUTPUT ===
      {
        id: "output_quad",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 }, // Additive
        contextId: "ctx_output",
      },
    ],
    edges: [],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_rate"],
        collapsed: false,
      },
      {
        id: "ctx_init",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 600 },
        nodeIds: [
          "init_position",
          "init_lifetime",
          "init_size",
          "init_color",
          "init_velocity",
          "init_angular",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update",
        type: "vfx.update",
        position: { x: 50, y: 850 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "update_vortex",
          "update_attract",
          "update_color",
          "update_size",
          "update_turbulence",
        ],
        collapsed: false,
      },
      {
        id: "ctx_output",
        type: "vfx.output",
        position: { x: 50, y: 1400 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_quad"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow1",
        fromContextId: "ctx_spawn",
        fromPortId: "spawn_event",
        toContextId: "ctx_init",
        toPortId: "spawn_event",
      },
      {
        id: "flow2",
        fromContextId: "ctx_init",
        fromPortId: "particle",
        toContextId: "ctx_update",
        toPortId: "particle",
      },
      {
        id: "flow3",
        fromContextId: "ctx_update",
        fromPortId: "particle",
        toContextId: "ctx_output",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init: { capacity: 5000, boundsMode: "auto" },
    },
    capacity: { maxParticles: 5000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}
